import { useCallback, useLayoutEffect, useRef, useState } from "react";

import { ServiceRequest } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import * as Sentry from "@sentry/react";

import { LABS_RESULTS_FETCH_TOAST } from "@/health-gorilla/constants/labs-refresh-messages";
import { useAppDispatch } from "@/store";
import { loadLabOrders } from "@/store/slices/lab-orders";
import { toast } from "@/utils/notifications";

export interface LabOrderResultsPage {
  results: ServiceRequest[];
  total: number;
}

export type FetchLabOrderResultsPage = () => Promise<LabOrderResultsPage | undefined>;

export interface UseLabOrderResultsOptions {
  /**
   * Fetches the page of lab orders currently on screen. Returns `undefined` when the practice or
   * client id is not available yet. Memoize this on everything the request depends on. A new
   * function identity triggers a refetch, which is how a page, status filter or client change
   * reaches the server.
   */
  fetchPage: FetchLabOrderResultsPage;
  /** Receives the server's total result count, for the caller's pagination. */
  onTotalChange: (total: number) => void;
}

export interface UseLabOrderResultsResult {
  /** True on the first load and whenever `fetchPage` changes — a new page, filter or client. */
  isLoading: boolean;
  /** True only while a `refresh()` refetch is in flight, so the header control alone reports it. */
  isRefreshing: boolean;
  /** Refetches the current page without clearing the rows already on screen. */
  refresh: () => void;
}

/**
 * Loads one page of lab orders into the store and keeps it refreshable. Shared by the client Results
 * panel and the practice Results page so both fetch, report progress and fail the same way.
 *
 * A refresh re-runs the same request and leaves the rows on screen, so the button does not feel like
 * the page reload it replaces. A load clears the table and shows its loading state.
 *
 * @param options.fetchPage memoized fetcher for the page on screen
 * @param options.onTotalChange receives the server's total result count
 * @returns the list's loading and refreshing flags, plus `refresh` for the header control
 */
export function useLabOrderResults({
  fetchPage,
  onTotalChange,
}: UseLabOrderResultsOptions): UseLabOrderResultsResult {
  const dispatch = useAppDispatch();
  const medplum = useMedplum();
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Only the newest request writes its results. Page and filter changes can come back out of order,
  // so an older response must not replace the rows a newer one already put on screen.
  const latestRequestId = useRef(0);

  const runFetch = useCallback(
    async (isRefresh: boolean) => {
      const requestId = (latestRequestId.current += 1);

      if (isRefresh) {
        // The fetchers pass `cache: "no-cache"`, but nothing enforces that. Clear the cached
        // searches here too, so a refresh always reaches the server.
        medplum.invalidateSearches("ServiceRequest");
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }

      try {
        const page = await fetchPage();
        if (requestId !== latestRequestId.current || !page) return;

        dispatch(loadLabOrders(page.results));
        onTotalChange(page.total);
      } catch (error) {
        if (requestId !== latestRequestId.current) return;

        Sentry.captureException(error);
        toast({
          type: "error",
          ...(isRefresh
            ? LABS_RESULTS_FETCH_TOAST.refreshFailed
            : LABS_RESULTS_FETCH_TOAST.loadFailed),
          autoClose: false,
        });
      } finally {
        // Clear both flags, not just this call's own. A superseded load never reaches this line, so
        // the last request to finish has to clear what the other one set.
        if (requestId === latestRequestId.current) {
          setIsLoading(false);
          setIsRefreshing(false);
        }
      }
    },
    [dispatch, medplum, fetchPage, onTotalChange],
  );

  // This runs before the browser paints. A page or filter change shows the loading state right away,
  // rather than the previous page's rows for one frame.
  useLayoutEffect(() => {
    void runFetch(false);

    // Retire any request still in flight when the request changes or the list unmounts.
    return () => {
      latestRequestId.current += 1;
    };
  }, [runFetch]);

  const refresh = useCallback(() => {
    void runFetch(true);
  }, [runFetch]);

  return { isLoading, isRefreshing, refresh };
}
